<script lang="ts">
    import {createEventDispatcher, onMount} from 'svelte';
    import WordFacts from '$lib/components/browse/WordCard/WordFacts.svelte';
    import KnownLearnRemove from '$lib/components/browse/WordCard/KnownLearnRemove.svelte';
    import Translation from '$lib/components/browse/WordCard/Translation.svelte';
    import SentencesMobile from '$lib/components/browse/WordCard/_Mobile/SentencesMobile.svelte';
    import WordCountMobile from '$lib/components/browse/WordCard/_Mobile/WordCountMobile.svelte';
    import NavigateCloseBtns from '$lib/components/browse/WordCard/_Mobile/NavigateCloseBtns.svelte';
    import {afterNavigate, beforeNavigate} from '$app/navigation';
    import type {WordIdToSentences} from '$lib/data/wordIdToSentences';
    import type {UserWordsMapStore} from '$lib/classes/stores/get-user-words-map';
    import type {DisplayWords} from '$lib/stores/displayWords';
    import type {FilterSettings} from '$lib/stores/filterSettings';
    import {Noun} from '$lib/classes/data/word';
    import type {CurrentWord} from '$lib/stores/currentWord';
    import {type Device, DeviceType} from '$lib/stores/ui/device';

    const dispatch = createEventDispatcher();

    export let wordIdToSentences: WordIdToSentences
    export let userWordsMap: UserWordsMapStore
    export let currentWord: CurrentWord
    export let displayWords: DisplayWords
    export let filterSettings: FilterSettings
    export let device: Device

    onMount(() => {
        try {
            // @ts-ignore
            if (typeof window.Tawk_API?.hideWidget === 'function') {
                // @ts-ignore
                window.Tawk_API?.hideWidget()
            }
        } catch (error) {
            console.error('Couldn\'t hide Tawk', error)
        }

        return () => {
            try {
                // @ts-ignore
                if (typeof window.Tawk_API?.showWidget === 'function') {
                    // @ts-ignore
                    window.Tawk_API?.showWidget()
                }
            } catch (error) {
                console.error('Couldn\'t show Tawk', error)
            }
        }
    })

    let wordCardMobile: HTMLDivElement

    beforeNavigate((navigation) => {
        // NOTE necessary when '>> Upgrade' link to pricing in toast is clicked (?)
        // NOTE would look better after navigation, but needs to happen before
        //  because component doesn't exist anymore after navigation (but then it's not visible anymore anyway..?! >> TEST)
        // TODO change from client-side component API to conditional rendering via {#if}
        if (!navigation.to?.route.id?.includes('/browse')) {
            dispatch('close')
        }
    })

    afterNavigate(() => wordCardMobile.scrollTop = 0)

    $: if ($device === DeviceType.Desktop) dispatch('close')

    $: savedWordsCount = Object.values($userWordsMap).length.toLocaleString()

    $: cW = $currentWord!
    // TODO remove filterSettings.syllables
    $: word = cW.base.displayWord && !$filterSettings.syllables ? cW.base.displayWord.split(', ') :
        $filterSettings.syllables ? [cW.syllStr] :
            [cW.word]
    $: translations = cW.base.translations
    $: art = cW.art
    // TODO pass Word to WordFacts and read properties internally
    $: frequency = cW.base.frequency
    $: phonetic = cW.base.phonetic
    $: plural = cW instanceof Noun ? cW.plural : null
    $: id = cW.id
    $: userWord = $userWordsMap[id]
    $: sentences = wordIdToSentences[id] ?? []

    function handleKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape') dispatch('close')
    }
</script>

<svelte:window on:keydown={handleKeydown}/>

<div id="word-card-mobile"
     data-testid="word-card"
     bind:this={wordCardMobile}
     style={`--color-accent: var(--color-${art});`}
>

    <div id="word-card-modal-top">
        <div id="word-facts-wrapper">
            <WordFacts
                    art={art}
                    frequency={frequency}
                    level={cW.base.level}
                    phonetic={phonetic}
                    plural={plural}
            />
        </div>

        <div id="word-card-modal-top-right">
            <WordCountMobile {savedWordsCount}/>
            <KnownLearnRemove
                    {id}
                    {userWord}
            />
        </div>
    </div>

    <div id="translation-wrapper-mobile">
        <Translation
                currentWord={currentWord}
                word={word}
                translations={translations}
                filterSettings={filterSettings}
                device={device}
        />
    </div>

    {#key $currentWord}
        <SentencesMobile
                currentWord={currentWord}
                userWordsMap={userWordsMap}
                {sentences}
                {userWord}
        />
    {/key}

    <NavigateCloseBtns
            currentWord={currentWord}
            displayWords={displayWords}
            on:close={() => dispatch('close')}
    />

</div>

<style>
    #word-card-mobile {
        position: fixed;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        background: var(--bg);
        overflow-y: scroll;
        z-index: var(--z-index-word-card-mobile);
        /*border seems to prevent that address bar hides on scroll in mobile browser*/
        border-top: 1px solid transparent;
    }

    #word-card-modal-top {
        display: flex;
        justify-content: space-between;
        padding: 1.5rem;
        padding-bottom: 0;
    }

    #translation-wrapper-mobile {
        padding: 0 1.5rem 4rem 1.5rem;
        /*border: 1px solid mediumpurple;*/
    }

    #word-facts-wrapper {
        display: flex;
        flex-direction: column;
    }

    #word-card-modal-top-right {
        display: flex;
        flex-direction: column;
    }

</style>